import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { returnsAPI } from '../../api/returns';
import {
    ArrowLeft, Package, RotateCcw, AlertCircle, ChevronRight,
} from 'lucide-react';
import { formatDate } from '../../utils/date';
import { formatPrice } from '../../utils/utilityFunctions';
import toast from '../../utils/toast';
import type { ReturnRequest } from '../../types';

const STATUS_META: Record<ReturnRequest['status'], { label: string; classes: string }> = {
    pending: { label: 'Pending vendor', classes: 'bg-amber-100 text-amber-800' },
    approved: { label: 'Approved', classes: 'bg-blue-100 text-blue-800' },
    partially_approved: { label: 'Partially approved', classes: 'bg-blue-100 text-blue-800' },
    auto_approved: { label: 'Auto-approved', classes: 'bg-blue-100 text-blue-800' },
    rejected: { label: 'Rejected', classes: 'bg-red-100 text-red-800' },
    cancelled: { label: 'Cancelled', classes: 'bg-gray-100 text-gray-700' },
    in_transit: { label: 'Pickup in progress', classes: 'bg-indigo-100 text-indigo-800' },
    completed: { label: 'Completed', classes: 'bg-green-100 text-green-800' },
    failed: { label: 'Failed', classes: 'bg-red-100 text-red-800' },
};

const ReturnsList: React.FC = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<ReturnRequest[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setItems(await returnsAPI.list());
            } catch {
                toast.error('Failed to load returns');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div>
                <div className="max-w-4xl mx-auto px-4 py-8">
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <button
                    onClick={() => navigate('/orders')}
                    className="flex items-center text-blue-600 hover:text-blue-700 font-medium mb-4"
                >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back to Orders
                </button>

                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">My Returns</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Track your refund and replacement requests.
                    </p>
                </div>

                {items.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                        <RotateCcw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <h2 className="text-lg font-semibold text-gray-900">No returns yet</h2>
                        <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
                            You can request a return from any delivered order.
                        </p>
                        <Link
                            to="/orders"
                            className="inline-block mt-6 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                        >
                            View Orders
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map(r => {
                            const meta = STATUS_META[r.status];
                            return (
                                <Link
                                    key={r.id}
                                    to={`/returns/${r.id}`}
                                    className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors"
                                >
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                                        {r.product_image ? (
                                            <img src={r.product_image} alt={r.product_name} className="w-full h-full object-cover" />
                                        ) : (
                                            <Package className="w-6 h-6 text-gray-400" />
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="font-medium text-gray-900 truncate">
                                                    {r.product_name}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    Order #{r.order_number} · Requested {formatDate(r.created_at)}
                                                </p>
                                            </div>
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.classes}`}>
                                                {meta.label}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                            <span className="capitalize">{r.kind}</span>
                                            <span>·</span>
                                            <span>
                                                {r.approved_amount
                                                    ? formatPrice(r.approved_amount)
                                                    : formatPrice(r.item_refund_amount)}
                                            </span>
                                            {r.return_code && (
                                                <>
                                                    <span>·</span>
                                                    <span className="font-mono">{r.return_code}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReturnsList;